import { useState, useEffect } from "react";
import {
  useSearchMessages,
  useMessageContext,
  SearchMessagesParams,
} from "@/lib/api/admin";
import { MentorshipDetails } from "@/lib/api/mentorships";
import { SearchForm } from "./search-form";
import { SearchResults } from "./search-results";
import { ContextModal } from "./context-modal";

interface MessageSearchProps {
  mentorshipId: string;
  mentorship: MentorshipDetails;
}

export function MessageSearch({
  mentorshipId,
  mentorship,
}: MessageSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOptions, setSearchOptions] = useState<
    Partial<SearchMessagesParams>
  >({
    limit: 20,
    sort_field: "score",
  });
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(
    null,
  );
  const [showContextModal, setShowContextModal] = useState(false);

  const searchMutation = useSearchMessages(mentorshipId);
  const { data: contextData, isLoading: isContextLoading } = useMessageContext(
    mentorshipId,
    selectedMessageId || 0,
    10,
  );

  // Initialize state from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get("q") || "";
    const sortField = (urlParams.get("sort") as "score" | "ts") || "score";
    const limit = parseInt(urlParams.get("limit") || "20");
    const exactMatch = urlParams.get("exact") === "true";
    const reverse = urlParams.get("reverse") === "true";

    setSearchQuery(query);
    setSearchOptions({
      limit,
      sort_field: sortField,
      exact_match: exactMatch,
      reverse,
    });

    // Auto-search if there's a query in URL
    if (query.trim()) {
      searchMutation.mutate({
        query,
        limit,
        sort_field: sortField,
        exact_match: exactMatch,
        reverse,
      });
    }
  }, []);

  // Update URL parameters when search options change
  const updateUrlParams = (
    query: string,
    options: Partial<SearchMessagesParams>,
  ) => {
    const newParams = new URLSearchParams();

    if (query.trim()) {
      newParams.set("q", query);
    }

    if (options.sort_field && options.sort_field !== "score") {
      newParams.set("sort", options.sort_field);
    }

    if (options.limit && options.limit !== 20) {
      newParams.set("limit", options.limit.toString());
    }

    if (options.exact_match) {
      newParams.set("exact", "true");
    }

    if (options.reverse) {
      newParams.set("reverse", "true");
    }

    // Update URL without page reload
    const newUrl = `${window.location.pathname}${
      newParams.toString() ? "?" + newParams.toString() : ""
    }`;
    window.history.replaceState({}, "", newUrl);
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    const searchPayload = {
      query: searchQuery,
      ...searchOptions,
    };

    // Update URL parameters
    updateUrlParams(searchQuery, searchOptions);

    // Perform search
    searchMutation.mutate(searchPayload);
  };

  const handleSearchOptionsChange = (
    newOptions: Partial<SearchMessagesParams>,
  ) => {
    const updatedOptions = { ...searchOptions, ...newOptions };
    setSearchOptions(updatedOptions);

    // If there's an active search, update URL and re-search
    if (searchQuery.trim()) {
      updateUrlParams(searchQuery, updatedOptions);
      searchMutation.mutate({
        query: searchQuery,
        ...updatedOptions,
      });
    }
  };

  const handleViewContext = (messageId: number) => {
    setSelectedMessageId(messageId);
    setShowContextModal(true);
  };

  return (
    <div className="space-y-6 pb-20">
      <SearchForm
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchOptions={searchOptions}
        onSearchOptionsChange={handleSearchOptionsChange}
        onSearch={handleSearch}
        isSearching={searchMutation.isPending}
      />

      {searchMutation.data && (
        <SearchResults
          results={searchMutation.data.results}
          totalCount={searchMutation.data.total_count}
          searchQuery={searchQuery}
          mentorship={mentorship}
          onViewContext={handleViewContext}
        />
      )}

      <ContextModal
        isOpen={showContextModal}
        onClose={() => setShowContextModal(false)}
        contextData={contextData}
        isLoading={isContextLoading}
        mentorship={mentorship}
      />
    </div>
  );
}
